// src/components/Industrial/IndustrialPage.js
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './Industrialpage.module.css';
import ParticleNetwork from './components/ParticleNetwork';
import Seo from './components/Seo';

/* ─── Product data ─── */
const PRODUCTS = [
  {
    icon: '🔵',
    title: 'Silica Gel Desiccants',
    slug: 'silica-gel-desiccants-india',
    desc: 'High-purity silica gel packets absorbing up to 40% of their weight in moisture. Ideal for electronics, pharmaceuticals, leather goods and precision instruments.',
    tags: ['2g / 5g / 10g / 50g packs', 'Indicating & Non-indicating', 'Food-grade available'],
  },
  {
    icon: '🟠',
    title: 'Container Desiccants',
    slug: 'container-desiccants-manufacturer',
    desc: 'Calcium chloride-based hanging desiccant bags engineered for shipping containers and warehouses. Prevents cargo sweat, corrosion and mold during long sea voyages.',
    tags: ['500g / 1kg units', 'Hooks & adhesive strips', 'Valid for 60–90 day voyages'],
  },
  {
    icon: '🟣',
    title: 'Moisture Barrier Bags',
    slug: 'moisture-barrier-packaging-bags',
    desc: 'Multi-layer foil laminate bags with superior WVTR ratings for long-term storage of electronic components, military equipment and industrial machinery parts.',
    tags: ['MIL-PRF-81705 compliant', 'Heat-sealable', 'Custom sizes'],
  },
  {
    icon: '🟡',
    title: 'Humidity Indicator Cards',
    slug: 'humidity-indicator-cards-hic',
    desc: 'Visual cobalt-free HIC cards that provide instant, accurate read-out of relative humidity inside sealed packaging — no instruments required.',
    tags: ['10% / 20% / 30% / 60% RH', 'RoHS compliant', 'Pack of 125'],
  },
  {
    icon: '🟢',
    title: 'Oxygen Absorbers',
    slug: 'oxygen-absorbers-food-industrial',
    desc: 'Iron-based oxygen absorber sachets extending shelf life of food, nutraceuticals and sensitive materials by maintaining sub-0.1% O₂ environments.',
    tags: ['50cc to 2000cc capacity', 'Food-safe certified', 'Bulk supply available'],
  },
  {
    icon: '🔶',
    title: 'Custom Industrial Packs',
    slug: 'custom-desiccant-packs-oem',
    desc: 'OEM-grade custom desiccant solutions engineered to your exact specifications — size, material, weight and branding. Ideal for white-label and private-label requirements.',
    tags: ['MOQ flexible', 'Pilot sampling available'],
  },
];

/* ─── Industries served ─── */
const INDUSTRIES = [
  { icon: '💊', label: 'Pharmaceuticals' },
  { icon: '⚡', label: 'Electronics & Semiconductors' },
  { icon: '🚢', label: 'Shipping & Logistics' },
  { icon: '🌾', label: 'Food & Agro' },
  { icon: '⚙️', label: 'Heavy Machinery' },
  { icon: '🪖', label: 'Defence & Aerospace' },
  { icon: '👜', label: 'Leather & Apparel' },
  { icon: '🔬', label: 'Medical Devices' },
];

/* ─── Why Choose Us ─── */
const USPs = [
//   { num: '01', title: 'ISO-Grade Quality', body: 'Our desiccants conform to MIL-D-3464 and ISO 9001 production standards, ensuring batch-to-batch consistency.' },
  { num: '01', title: 'Top-Grade Quality', body: 'Our desiccants conform to top of the line production standards, ensuring batch-to-batch consistency.' },
  { num: '02', title: 'Pan-India Supply Chain', body: 'Warehoused in Kolkata with distribution reach across India, Singapore, Taiwan and Hong Kong.' },
  { num: '03', title: 'Bulk & Wholesale Pricing', body: 'Volume-tiered pricing with flexible MOQs to suit both SMEs and large enterprises.' },
  { num: '04', title: 'Tech-Backed Traceability', body: 'Powered by BETA Edge\'s own software stack — every lot is digitally traceable from production to delivery.' },
];

/* ─── Lead Form ─── */
const LeadForm = () => {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', product: '', message: '' });
  const [status, setStatus] = useState(null);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    // Replace this with your actual Firebase / API call
    await new Promise(r => setTimeout(r, 1200));
    setStatus('success');
  };

  if (status === 'success') {
    return (
      <div className={styles.formSuccess}>
        <div className={styles.successIcon}>✓</div>
        <h3>Enquiry Received!</h3>
        <p>Our industrial team will contact you within 24 hours.</p>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} aria-label="Product enquiry form">
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label htmlFor="ind-name">Full Name *</label>
          <input id="ind-name" name="name" type="text" required placeholder="John Doe" value={form.name} onChange={handleChange} />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="ind-company">Company / Organisation</label>
          <input id="ind-company" name="company" type="text" placeholder="Acme Pvt. Ltd." value={form.company} onChange={handleChange} />
        </div>
      </div>
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label htmlFor="ind-email">Business Email *</label>
          <input id="ind-email" name="email" type="email" required placeholder="you@company.com" value={form.email} onChange={handleChange} />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="ind-phone">Phone Number</label>
          <input id="ind-phone" name="phone" type="tel" placeholder="+91 98XXXXXXXX" value={form.phone} onChange={handleChange} />
        </div>
      </div>
      <div className={styles.formGroup}>
        <label htmlFor="ind-product">Product of Interest *</label>
        <select id="ind-product" name="product" required value={form.product} onChange={handleChange}>
          <option value="">— Select a product —</option>
          {PRODUCTS.map(p => <option key={p.slug} value={p.title}>{p.title}</option>)}
          <option value="General Enquiry">General / Bulk Enquiry</option>
        </select>
      </div>
      <div className={styles.formGroup}>
        <label htmlFor="ind-message">Message / Requirements</label>
        <textarea id="ind-message" name="message" rows={4} placeholder="Describe your requirement — quantity, application, delivery location…" value={form.message} onChange={handleChange} />
      </div>
      <button type="submit" className={styles.submitBtn} disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Send Enquiry →'}
      </button>
      <p className={styles.formNote}>We typically respond within 1 business day. Your data is never shared with third parties.</p>
    </form>
  );
};

/* ─── Main Page ─── */
const IndustrialPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const productsRef = useRef(null);
  const whyRef = useRef(null);
  const enquiryRef = useRef(null);

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <div className={styles.page}>
      <Seo
        title="BETA Edge Industrial | Moisture Absorbent Products & Desiccant Solutions India"
        description="BETA Edge Industrial supplies premium moisture absorbent products including silica gel desiccants, container desiccants, moisture barrier bags and humidity control solutions across India, Singapore and global markets."
        keywords="moisture absorbent products, desiccant bags India, silica gel packets manufacturer, industrial desiccants, container desiccant supplier, humidity control solutions, moisture control packaging, desiccant manufacturer India, bulk silica gel, oxygen absorbers"
        canonical="https://www.beta-edge.com/industrial"
        ogTitle="BETA Edge Industrial — Moisture Control Experts"
        ogDescription="Premium industrial desiccants, silica gel products and moisture control packaging for global supply chains."
        ogUrl="https://www.beta-edge.com/industrial"
        ogImage="https://www.beta-edge.com/og-industrial.jpg"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'BETA Edge Industrial',
            url: 'https://www.beta-edge.com/industrial',
            description: 'Manufacturer and supplier of moisture absorbent products, silica gel desiccants, container desiccants and humidity control solutions.',
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+91-9804619202',
              contactType: 'Sales',
              areaServed: ['IN', 'SG', 'TW', 'HK'],
              availableLanguage: ['English', 'Hindi'],
            },
            address: { '@type': 'PostalAddress', addressLocality: 'Kolkata', addressCountry: 'IN' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'OfferCatalog',
            name: 'BETA Edge Industrial Product Catalog',
            itemListElement: PRODUCTS.map((p) => ({
              '@type': 'Product',
              name: p.title,
              description: p.desc,
              url: `https://www.beta-edge.com/industrial#${p.slug}`,
            })),
          },
        ]}
      />
      <ParticleNetwork
        dotColor="#e87500"
        lineColor="232, 117, 0"
        cursorLineColor="255, 170, 80"
      />

      {/* ── Nav ── */}
      <nav className={styles.nav} aria-label="BETA Edge Industrial navigation">
        <div className={styles.navBrand}>
          <Link to="/" className={styles.brandBack} title="Back to BETA Edge Tech">← Tech</Link>
          <span className={styles.brandDivider}>|</span>
          <span className={styles.brandName}>BETA Edge <span className={styles.brandTag}>Industrial</span></span>
        </div>
        <button className={styles.burger} aria-label="Toggle menu" onClick={() => setMenuOpen(p => !p)}>☰</button>
        <div className={`${styles.navMenu} ${menuOpen ? styles.navMenuOpen : ''}`}>
          <button onClick={() => scrollTo(productsRef)}>Products</button>
          <button onClick={() => scrollTo(whyRef)}>Why Us</button>
          {/* <button onClick={() => scrollTo(enquiryRef)}>Get a Quote</button> */}
          <a href="mailto:connect@beta-edge.com" className={styles.navCta}>Email Us</a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className={styles.hero} role="banner">
        <div className={styles.heroBg} aria-hidden="true" />
        <div className={styles.heroContent}>
          <p className={styles.heroEyebrow}>BETA Edge Industrial Division</p>
          <h1 className={styles.heroHeadline}>
            Industrial Moisture<br />
            <span className={styles.heroAccent}>Control Solutions</span>
          </h1>
          <p className={styles.heroSub}>
            Premium desiccants, silica gel packets, container moisture absorbers and custom moisture-control packaging — trusted by manufacturers, logistics companies and exporters across India and Asia.
          </p>
          <div className={styles.heroActions}>
            {/* <button className={styles.heroCta} onClick={() => scrollTo(enquiryRef)}>Request a Free Sample</button> */}
            <button className={styles.heroSecondary} onClick={() => scrollTo(productsRef)}>Explore Products ↓</button>
          </div>
          <div className={styles.heroBadges} aria-label="Key certifications">
            <span>✓ ISO-Grade Quality</span>
            <span>✓ Bulk Orders Welcome</span>
            <span>✓ Pan-India Delivery</span>
            <span>✓ Export Ready</span>
          </div>
        </div>
      </header>

      {/* ── Industries ── */}
      <section className={styles.industries} aria-label="Industries we serve">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>Sectors We Serve</p>
          <h2 className={styles.sectionTitle}>Moisture Control Across Every Industry</h2>
          <div className={styles.industryGrid}>
            {INDUSTRIES.map(ind => (
              <div key={ind.label} className={styles.industryChip}>
                <span className={styles.industryIcon}>{ind.icon}</span>
                <span>{ind.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Products ── */}
      <section className={styles.products} ref={productsRef} aria-label="Product catalogue" id="products">
        <div className={styles.sectionInner}>
          {/* <p className={styles.sectionEyebrow}>Product Catalogue</p> */}
          <h2 className={styles.sectionTitle}>Desiccant & Moisture Absorbent Products</h2>
          <p className={styles.sectionSub}>
            BETA Edge Industrial offers a comprehensive range of moisture absorbent products engineered for demanding industrial environments. Browse our core product lines below.
          </p>
          <div className={styles.productGrid}>
            {PRODUCTS.map(p => (
              <article key={p.slug} className={styles.productCard} aria-label={p.title}>
                <div className={styles.productIcon}>{p.icon}</div>
                <h3 className={styles.productTitle}>{p.title}</h3>
                <p className={styles.productDesc}>{p.desc}</p>
                <ul className={styles.productTags}>
                  {p.tags.map(t => <li key={t}>{t}</li>)}
                </ul>
                {/* Get a Quote Button */}
                {/* <button
                  className={styles.productEnquire}
                  onClick={() => { enquiryRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
                >
                  Get a Quote
                </button> */}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className={styles.why} ref={whyRef} aria-label="Why choose BETA Edge Industrial" id="why-us">
        <div className={styles.sectionInner}>
          {/* <p className={styles.sectionEyebrow}>Our Advantages</p> */}
          <h2 className={styles.sectionTitle}>Why Leading Companies Choose BETA Edge Industrial</h2>
          <div className={styles.uspGrid}>
            {USPs.map(u => (
              <div key={u.num} className={styles.uspCard}>
                <span className={styles.uspNum}>{u.num}</span>
                <h3>{u.title}</h3>
                <p>{u.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Lead Form ── */}
      {/* <section className={styles.enquiry} ref={enquiryRef} aria-label="Product enquiry form" id="enquiry">
        <div className={styles.sectionInner}>
          <p className={styles.sectionEyebrow}>Get in Touch</p>
          <h2 className={styles.sectionTitle}>Request a Quote or Free Sample</h2>
          <p className={styles.sectionSub}>
            Fill in the form below and our industrial supply team will get back to you within 24 hours with pricing, technical datasheets and sample availability.
          </p>
          <LeadForm />
        </div>
      </section> */}

      {/* ── Contact Strip ── */}
      <section className={styles.contactStrip} aria-label="Contact information">
        <div className={styles.stripInner}>
          <div className={styles.stripItem}>
            <span className={styles.stripIcon}>📍</span>
            <div>
              <strong>Global & Innovation Centre</strong>
              <p>Kolkata, India</p>
            </div>
          </div>
          <div className={styles.stripItem}>
            <span className={styles.stripIcon}>📞</span>
            <div>
              <strong>Call / WhatsApp</strong>
              <p><a href="tel:+919804619202">(+91) 9804 619 202</a></p>
              <p><a href="tel:+6591673307">(+65) 9167 3307</a></p>
            </div>
          </div>
          <div className={styles.stripItem}>
            <span className={styles.stripIcon}>✉️</span>
            <div>
              <strong>Email</strong>
              <p><a href="mailto:connect@beta-edge.com">connect@beta-edge.com</a></p>
            </div>
          </div>
          <div className={styles.stripItem}>
            <span className={styles.stripIcon}>🌐</span>
            <div>
              <strong>Nexus Points</strong>
              <p>India · Singapore · Taiwan · Hong Kong</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className={styles.footer} role="contentinfo">
        <p>
          © {new Date().getFullYear()} <strong>BETA Edge Industrial</strong> — A division of BETA Edge Inc.
          &nbsp;|&nbsp; <Link to="/">Back to Tech &amp; Software →</Link>
        </p>
        <p className={styles.footerKeywords}>
          Moisture Absorbent Products · Desiccant Bags · Silica Gel India · Container Desiccants · Industrial Moisture Control
        </p>
        <div className={styles.footerStars}>★★★★★</div>
      </footer>
    </div>
  );
};

export default IndustrialPage;